if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);

        if (preventFocus) this.openedBy = null;
        super.hide(); // removes [open] — CSS transition-out begins

        // Unsubscribe from variant changes
        if (this._variantUnsub) {
          this._variantUnsub();
          this._variantUnsub = null;
        }

        // Clear content after transition completes (matches CSS 0.25s)
        setTimeout(() => {
          this.modalContent.innerHTML = '';
          this.modalContent.classList.remove('ff-qa-loading');
        }, 250);
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        // Show modal immediately with loading spinner
        this.modalContent.classList.add('ff-qa-loading');
        super.show(opener);

        // Build fetch URL — respect card swatch selection if present
        let productUrl = opener.getAttribute('data-product-url');
        const card = opener.closest('.card-wrapper');
        if (card) {
          const checkedSwatch = card.querySelector('input.card-swatch:checked[data-variant-id]');
          if (checkedSwatch) {
            const variantId = checkedSwatch.getAttribute('data-variant-id');
            if (variantId) {
              try {
                const url = new URL(productUrl, window.location.origin);
                url.searchParams.set('variant', variantId);
                productUrl = url.pathname + url.search;
              } catch (e) {
                productUrl = productUrl.split('?')[0] + '?variant=' + variantId;
              }
            }
          }
        }

        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.modalContent.classList.remove('ff-qa-loading');

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // Sync view-details link with the currently selected variant
            const syncViewDetails = (variantId) => {
              const viewDetails = this.modalContent.querySelector('.product__view-details');
              if (!viewDetails || !variantId) return;
              try {
                const url = new URL(viewDetails.href, window.location.origin);
                url.searchParams.set('variant', variantId);
                viewDetails.href = url.toString();
              } catch (e) {
                viewDetails.href = viewDetails.href.split('?')[0] + '?variant=' + variantId;
              }
            };

            // Immediate sync — read variant from the form's hidden input
            const variantInput = this.modalContent.querySelector('input[name="id"]');
            if (variantInput && variantInput.value) {
              syncViewDetails(variantInput.value);
            }

            // Subscribe to subsequent variant changes
            if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              if (this._variantUnsub) this._variantUnsub();
              this._variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
                if (data.variant) syncViewDetails(data.variant.id);
              });
            }
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.simplifyGallery(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      simplifyGallery(productElement) {
        const mediaWrapper = productElement.querySelector('.product__media-wrapper');
        if (!mediaWrapper) return;

        // Strip the FF full-bleed class that applies negative margins
        mediaWrapper.classList.remove('ff-media-fix-gaps');
        // Add our clean class for simple styling
        mediaWrapper.classList.add('ff-qa-media');

        // Strip slider/peek classes from the media list to kill the scroll system
        const mediaList = mediaWrapper.querySelector('.product__media-list');
        if (mediaList) {
          mediaList.classList.remove('slider', 'slider--mobile', 'grid--peek', 'contains-media');
        }

        // Remove zoom buttons, arrows, thumbnails — keep only the image slides
        mediaWrapper.querySelectorAll(
          '.ff-zoom-icon, .ff-mobile-zoom, .ff-media-arrows, .ff-gallery-main > .thumbnail-slider, slider-component.thumbnail-slider'
        ).forEach(el => el.remove());
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}